import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import {
  Point,
  TextStyle,
  DisplayObject,
  Container as RawContainer,
  Text as RawText,
  FederatedPointerEvent
} from "pixi.js";
import { Container, Graphics, Text } from "@pixi/react";
import { Viewport } from "pixi-viewport";
import { useTranslation } from "react-i18next";
import { useGeometry } from "../../Hooks/useGeometry";
import { ZoneEdgeHandles } from "./ZoneEdgeHandles";
import { ZoneEdgeSplitter } from "./ZoneEdgeSplitter";
import { ConferenceZoneWarningMark } from "./ZoneWarnGraphic";
import { FavoriteStarGraphic } from "../FavoriteStarGraphic";
import { ToolSelection, ZoneColorMode } from "../Views/CreateFloorPlanView/CreateFloorPlanView";
import { ZoneVariant } from "../../Domain/Types/FloorPlan/ZoneVariant.type";
import { ColorOverlayFilter } from "pixi-filters";
import { createStripedTexture } from "./functions/ZoneTextureColorize.function";
import { IPlaceSchedule } from "../../Domain/Types/FloorPlan/PlaceSchedule";
import { CompanyUnits } from "../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { getMajorityDepartmentColor } from "./functions/getMajorityDepartmentColor.function";
import { resolveZonePolygonFillColor } from "./functions/fillColorByVariant.function";
import { IZonePermission } from "../../Domain/Types/FloorPlan/ZonePermission.type";
import { usePointerDrag } from "../../Hooks/usePointerDrag";

/** Smaller than `warnScale` so the favorite star reads lighter than the conference warning triangle. */
const ZONE_FAVORITE_STAR_SCALE_FACTOR = 0.72;
const ZONE_FAVORITE_STAR_OFFSET = 28;

function ZoneCentroidBookingOverlays({
  showConferenceWarning,
  isFavorite,
  warnScaleEffective,
  warnScale,
  setIsHoverWarning,
  warningEventMode
}: Readonly<{
  showConferenceWarning: boolean;
  isFavorite: boolean;
  warnScaleEffective: number;
  warnScale?: number;
  setIsHoverWarning?: (h: boolean) => void;
  warningEventMode?: "none" | "static";
}>) {
  if (showConferenceWarning && isFavorite) {
    return (
      <>
        <Container x={-ZONE_FAVORITE_STAR_OFFSET * warnScaleEffective}>
          <ConferenceZoneWarningMark
            warnScale={warnScale}
            setIsHoverWarning={setIsHoverWarning}
            eventMode={warningEventMode}
          />
        </Container>
        <Container x={ZONE_FAVORITE_STAR_OFFSET * warnScaleEffective}>
          <FavoriteStarGraphic starScale={warnScaleEffective * ZONE_FAVORITE_STAR_SCALE_FACTOR} />
        </Container>
      </>
    );
  }
  if (showConferenceWarning) {
    return (
      <ConferenceZoneWarningMark
        warnScale={warnScale}
        setIsHoverWarning={setIsHoverWarning}
        eventMode={warningEventMode}
      />
    );
  }
  if (isFavorite) {
    return <FavoriteStarGraphic starScale={warnScaleEffective * ZONE_FAVORITE_STAR_SCALE_FACTOR} />;
  }
  return null;
}

interface ZoneContainerProps {
  id: number;
  inventoryId?: number;
  walls: Point[];
  editMode?: boolean;
  onPointerUp?: (id: number) => void;
  onClick?: (id: number, mousePosition: Point, inventoryId?: number) => void;
  isSelected?: boolean;
  /** Override fill when selected (e.g. change-room dialog). Falls back to default selection blue. */
  selectedFillColor?: number;
  /** Highlight without full selection semantics (e.g. current booking room). */
  isReferenceHighlighted?: boolean;
  referenceFillColor?: number;
  referenceFillAlpha?: number;
  isClickable?: boolean;
  viewport?: Viewport;
  onZoneWallsChange?: (id: number, newWalls: Point[]) => void;
  onPointerEnter?: (zoneId: number) => void;
  onPointerLeave?: (zoneId: number) => void;
  showId?: boolean;
  disabled?: boolean;
  zoneType: number;
  showWarning?: boolean;
  /** User booking favorite for this zone inventory */
  isFavorite?: boolean;
  /** When false, warning icon does not capture pointer events (clicks pass to zone). Default true. */
  interactiveConferenceWarning?: boolean;
  setIsHoverWarning?: (h: boolean) => void;
  activeTouchCount?: number;
  dragThresholdPx?: number;
  tool?: ToolSelection;
  warnScale?: number;
  variant?: ZoneVariant;
  colorOverlay?: number;
  zoneColorMode?: ZoneColorMode;
  assignedPlaces?: IPlaceSchedule[];
  departmentData?: CompanyUnits[];
  permissions?: IZonePermission[];
}

export function ZoneContainer({
  id,
  inventoryId,
  walls,
  editMode = false,
  onPointerUp,
  onClick,
  isSelected = false,
  selectedFillColor,
  isReferenceHighlighted = false,
  referenceFillColor,
  referenceFillAlpha,
  isClickable = false,
  onZoneWallsChange,
  onPointerEnter,
  onPointerLeave,
  showId = false,
  disabled,
  zoneType,
  showWarning,
  isFavorite = false,
  interactiveConferenceWarning = true,
  setIsHoverWarning,
  activeTouchCount,
  dragThresholdPx = 0,
  tool,
  warnScale,
  viewport,
  variant,
  colorOverlay,
  zoneColorMode = "NORMAL",
  assignedPlaces = [],
  departmentData = [],
  permissions = []
}: PropsWithChildren<ZoneContainerProps>) {
  const { centroid } = useGeometry();
  const { t } = useTranslation();

  const zoneContainerRef = useRef<RawContainer<DisplayObject> | null>(null);
  const textRef = useRef<RawText>(null);

  const warnScaleEffective = warnScale ?? 0.7;
  const showConferenceWarning = zoneType === 3 && !!showWarning;
  const selectedColor = 0x0000ff;
  const lineColor = editMode ? selectedColor : 0x000000;
  const alphaPerType = zoneType === 3 ? 0.5 : 0.3;
  const lineColorCheckingInven = inventoryId === 0 ? 0xff0000 : lineColor;
  const zoneFillColor = resolveZonePolygonFillColor({
    zoneType,
    variant,
    isSelected,
    disabled,
    selectedFillColor,
    isReferenceHighlighted,
    referenceFillColor
  });
  const showHighlightOverlay = isSelected || isReferenceHighlighted;
  const isCurrentBookingHighlight = isReferenceHighlighted && !isSelected;
  const zoneFillAlpha = isCurrentBookingHighlight
    ? referenceFillAlpha ?? alphaPerType
    : alphaPerType;

  const [coordinates, setCoordinates] = useState<Point[] | undefined>(walls);

  const centroidPoint = useMemo(() => {
    if (!coordinates) return;
    return centroid(coordinates);
  }, [coordinates]);

  const { hasDragged, handlePointerDown, handlePointerMove, resetPointerGesture } =
    usePointerDrag(dragThresholdPx);
  const [isZoneEdgeUpdating, setIsZoneEdgeUpdating] = useState<boolean>(false);

  // Color Overlay Filter to show capacity rates
  const capacityFilters = useMemo(() => {
    if (!colorOverlay) return [];
    return [new ColorOverlayFilter(colorOverlay, 0.8)];
  }, [colorOverlay]);

  useEffect(() => {
    setCoordinates(walls);
  }, [walls]);

  function handleZoneClick(e: FederatedPointerEvent) {
    onClick?.(id, e.global, inventoryId); // removed the condition if(!isDragging)
  }

  const handlePointerEnter = () => onPointerEnter?.(id);
  const handlePointerLeave = () => onPointerLeave?.(id);
  function handleZonePointerUp(e: FederatedPointerEvent) {
    // prohibit selection when multi touches or when zone edge is being updated
    if ((activeTouchCount && activeTouchCount > 1) || isZoneEdgeUpdating) {
      resetPointerGesture();
      return;
    }

    // check tool selection and if dragging is not necessary then quickly handle pointer up
    if (hasDragged() && tool !== undefined) {
      resetPointerGesture();
      return;
    }
    resetPointerGesture();

    onPointerUp?.(id);
    handleZoneClick(e);
  }

  function moveCoordinate(index: number, newPosition: Point) {
    // convert screen space to world space
    const newPositionWorld = viewport?.toWorld(newPosition.x, newPosition.y);
    if (!newPositionWorld) return;

    // update coordinates
    setCoordinates(p => {
      if (!p) return coordinates;
      const updatedCoordinates = [...p];
      updatedCoordinates[index].x = newPositionWorld.x;
      updatedCoordinates[index].y = newPositionWorld.y;
      return updatedCoordinates;
    });
  }

  function handleCommitMove() {
    if (!coordinates) return;
    // convert the points back to a wall
    onZoneWallsChange?.(id, coordinates);
  }

  function handleAddPoint(newPoints: Point[]) {
    setCoordinates(newPoints);
    onZoneWallsChange?.(id, newPoints);
  }

  return (
    <>
      {coordinates && (
        <Container
          data-testid={"base-zone-container"}
          ref={zoneContainerRef}
          eventMode={isClickable ? "static" : "none"}
          onpointerup={handleZonePointerUp}
          onpointerdown={handlePointerDown}
          onpointerupoutside={resetPointerGesture}
          onpointercancel={resetPointerGesture}
          onpointerover={handlePointerEnter}
          onpointerout={handlePointerLeave}
          onglobalpointermove={handlePointerMove}
        >
          {/* zone graphic polygon */}
          <Graphics
            data-testid="zone-container-grp-polygon"
            cursor={isClickable ? "pointer" : "default"}
            filters={zoneType === 3 ? capacityFilters : null} // if conferencezone, apply capacityFilter on report module
            draw={g => {
              g.clear();

              const majDepColor = getMajorityDepartmentColor(
                assignedPlaces,
                zoneType === 3,
                permissions,
                departmentData
              );

              // Zone Color Mode : By department
              if (zoneColorMode === "BYDEPARTMENT" && majDepColor.length > 0) {
                g.beginTextureFill({ texture: createStripedTexture(majDepColor) });
                g.drawPolygon(coordinates);
                g.endFill();
                if (showHighlightOverlay) {
                  g.beginFill(zoneFillColor, isCurrentBookingHighlight ? zoneFillAlpha : 0.2);
                  g.lineStyle(2, lineColorCheckingInven);
                  g.drawPolygon(coordinates);
                  g.endFill();
                }
              } else {
                // Zone Color Mode : NORMAL
                g.beginFill(zoneFillColor, zoneFillAlpha);
              }
              g.lineStyle(2, lineColorCheckingInven);
              g.drawPolygon(coordinates);
              g.endFill();
            }}
          />

          {/** centroid */}
          {!showWarning && centroidPoint && (
            <Graphics
              data-testid={"centroid"}
              draw={g => {
                g.clear();
                g.lineStyle(1, 0xff00ff);
                g.drawRect(centroidPoint.x - 2.5, centroidPoint.y - 2.5, 5, 5);
                return g;
              }}
            />
          )}

          {/** edit and add points */}
          {editMode && (
            <>
              <ZoneEdgeHandles
                coordinates={coordinates}
                onMove={moveCoordinate}
                viewport={viewport}
                onCommitMove={handleCommitMove}
                setIsZoneEdgeUpdating={setIsZoneEdgeUpdating}
              />
              <ZoneEdgeSplitter
                viewport={viewport}
                coordinates={coordinates}
                onAddPoint={handleAddPoint}
                setIsZoneEdgeUpdating={setIsZoneEdgeUpdating}
              />
            </>
          )}

          {/* draw the inventory ID */}
          {(inventoryId !== undefined || inventoryId !== 0) && showId && (
            <Text
              data-testid={"text-inventoryId"}
              ref={textRef}
              text={inventoryId!.toString()}
              style={new TextStyle({ fontSize: 17, letterSpacing: 0.8, fill: 0x000000 })}
              resolution={3}
              x={coordinates[0].x + 10}
              y={coordinates[0].y + 10}
            />
          )}

          {/* error alarm when no zone inventory assigned */}
          {inventoryId === 0 && (
            <Text
              data-testid={"alarm-inventoryId"}
              ref={textRef}
              text={t("No Zone Inventory")}
              style={new TextStyle({ fontSize: 17, letterSpacing: 0.8, fill: 0xff0000 })}
              resolution={3}
              x={coordinates[0].x + 10}
              y={coordinates[0].y + 10}
            />
          )}

          {/* Conference zone: warning mark and/or favorite star at centroid (row when both) */}
          {isClickable && centroidPoint && (showConferenceWarning || isFavorite) && (
            <Container position={centroidPoint}>
              <ZoneCentroidBookingOverlays
                showConferenceWarning={showConferenceWarning}
                isFavorite={isFavorite}
                warnScaleEffective={warnScaleEffective}
                warnScale={warnScale}
                setIsHoverWarning={setIsHoverWarning}
                warningEventMode={interactiveConferenceWarning ? "static" : "none"}
              />
            </Container>
          )}
        </Container>
      )}
    </>
  );
}
